// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Sprint 62.8.0 — the page-size ceiling, in one place, because two places
 * disagreed and twelve surfaces broke.
 *
 * ## The defect
 *
 * `public-data`, `payments-evidence` and `platform-evidence` each capped
 * `page_size` at **100** in their own route schemas. Their pages ask for **200**
 * when loading a selector — the list behind a dropdown, which wants every row
 * rather than a page of them. So every one of those requests was a **400** on
 * every page view, for every tenant, from the day the modules shipped.
 *
 * Measured 2026-08-08: **12 call sites across 8 page files**, 10 of them against a
 * capped route.
 *
 * Eleven failed **silently** — the effects that load a selector catch and move on,
 * so the dropdown simply stayed empty. That is the defect 62.0.11 settled the
 * position on: a control an operator cannot use, on a page that looks finished. The
 * twelfth was visible, and it is how this was found — the readiness panel on the
 * open-data releases page renders *"This register could not be loaded. Reload the
 * page to try again"*, offering a reload that could never work, because the request
 * was not failing for a transient reason. **Reported by the user against the live
 * demo, not by any test.**
 *
 * Nothing compared the two numbers. The route schemas were correct in isolation and
 * the pages were correct in isolation; the contract between them existed only as
 * two literals in different files, and no check read both. `62.8.0`'s own
 * manufacturing routes capped at 200 and therefore worked — **by luck, not by
 * design**, which is the same shape as the pack-version bump this sprint also had
 * to make structural.
 *
 * ## Why one constant rather than twelve edits
 *
 * Lowering the clients to 100 would have made the requests succeed and left every
 * selector silently truncated at 100 rows — the identical defect one step further
 * out, and harder to see because nothing would error. Raising each route's literal
 * to 200 would have fixed today's twelve and left the next module free to pick its
 * own number.
 *
 * So the ceiling is declared once and imported by both sides. A route schema and a
 * client call that both read this cannot disagree, and there is no per-module number
 * for anybody to choose.
 */
/**
 * The largest `page_size` any list route accepts.
 *
 * Route schemas bound `page_size` with this. It is a **protection against an
 * unbounded query**, not a product decision: a caller asking for more rows than this
 * gets a 400 naming the parameter, rather than the server attempting an unbounded
 * read.
 */
export const MAX_LIST_PAGE_SIZE = 200;
/**
 * What a **selector** load asks for — the list behind a dropdown or a picker.
 *
 * Equal to the ceiling on purpose: a selector wants every row, and this is the most
 * it may have. Named separately from `MAX_LIST_PAGE_SIZE` because the two are
 * different intents that happen to share a number, and a later change to the
 * ceiling should not silently redefine what a selector requests.
 *
 * **A selector that needs more rows than this is truncated, and truncation in a
 * selector is silent by nature** — the operator sees a list, not a short list. Any
 * surface whose population can exceed this needs a searching or paginating control
 * rather than a bigger number, and raising the ceiling is not the fix. Recorded as
 * idea-catalog **#117** rather than pretended away here.
 *
 * **Sprint 64.61.0 closed #117, and the row was wrong about its own subject.** The
 * paragraph above is still true — a selector over a population larger than this is
 * truncated by construction — but the six sites the row cited were not being
 * truncated at 100. They were answering **500**: no clamp at all, and the query
 * string reaching `FindCursor.limit()`, which `mongodb@6` refuses. A truncated
 * picker now says so in rendered text; see {@link resolveListPageSize} for the
 * measurement.
 * (It cited **#106**, which is a real row about the housekeeping metric linter's
 * first-match-wins read and has nothing to do with page sizes. So the original was a
 * deferral to the **wrong** row rather than to a missing one — and the first correction
 * of it asserted the row did not exist, which was also wrong. Both errors are the same
 * shape: a cross-reference stated without opening the thing referred to. Gate E12.5
 * passes 2 and 3.)
 */
export const SELECTOR_PAGE_SIZE = MAX_LIST_PAGE_SIZE;
/** The page size a list route uses when the caller names none. */
export const DEFAULT_LIST_PAGE_SIZE = 20;
/**
 * Sprint 64.61.0 — the clamp is what makes a query string safe, and the services
 * without one do not clamp, they **throw**.
 *
 * ## What was measured
 *
 * Idea-catalog #117 was raised on the belief that a list service clamping
 * `page_size` at 100 answers a request for 500 with **HTTP 200 and 100 rows**,
 * silently. Re-measured at this sprint's kickoff against `mongodb@6.21.0` and a
 * real `mongo:7`, on the six sites the ceiling guard exempts as `clamped`:
 *
 * | request | measured |
 * |---|---|
 * | `PartnerOrgService.list(org, { page_size: 500 })` — numeric | 200, **150 of 150 rows**, no clamp at all |
 * | `GET /partner-marketplace/partner-orgs?page_size=500` | **`MongoInvalidArgumentError`** |
 * | `…?page_size=200` | **`MongoInvalidArgumentError`** |
 * | `…?page_size=25` | **`MongoInvalidArgumentError`** |
 * | `…` with no `page_size` | 200, 20 rows |
 *
 * `req.query` hands every value over as a **string**. `partner-marketplace/routes.ts`
 * casts it — `req.query as Record<string, string | undefined>` — into a parameter
 * object declaring `page_size?: number`, and the string travels untouched to
 * `FindCursor.limit()`, which refuses a non-number outright:
 *
 * ```js
 * // node_modules/mongodb/lib/cursor/find_cursor.js:364
 * if (typeof value !== 'number') {
 *   throw new error_1.MongoInvalidArgumentError('Operation "limit" requires an integer');
 * }
 * ```
 *
 * So the module's list surface has answered **500** to every paginated request
 * since it shipped, and the six exemptions describe a clamp that is not there.
 * The four services that *do* clamp — `Math.min(Math.max(params?.page_size ?? 20, 1), 100)`
 * — are string-safe **by accident**: `Math.max('500', 1)` coerces to `500`. The
 * clamp #117 filed as the defect is the only thing that was working.
 *
 * ## Why this is a shared function and not an eleventh local clamp
 *
 * R-43. `incident-response-maturity-service.ts` already carried this exact pair,
 * module-private, and argued in its own docblock that lifting it "would mean
 * exporting a third pagination surface **for one caller**". That was true at one
 * caller. It is now eleven, in one module, plus that one — so the count the
 * argument rested on has moved and the conclusion moves with it. The ceiling was
 * already shared; the two lines that read it now are too, and
 * `incident-response-maturity-service.ts` imports these rather than keeping its
 * copy.
 *
 * ## The bound is the parameter, not a sentence beside it
 *
 * `Math.min(MAX_LIST_PAGE_SIZE, …)` reads the constant. A prior sprint in this
 * repository wrote a measured worst case into a docblock and a smaller number
 * into the argument, and failed on precisely the case it had measured; there is
 * no numeric literal here for the two to disagree about.
 *
 * ## The fallback is a parameter, and that is not a convenience
 *
 * Gate E12.5 on batch 16 applied these resolvers to ~30 list services that had
 * been carrying `params?.page_size ?? N`. `N` was not always 20 — `categories`,
 * `purposes`, `retention` and `dm-audit` default to **50**, and several
 * maturity services to 25. A resolver with a hard-wired 20 would have coerced
 * the string correctly and *silently changed the default page of four shipped
 * tables from 50 rows to 20* — a behaviour change smuggled in under a
 * correctness fix, which is exactly the shape of thing that gets waved through
 * because the diff looks mechanical.
 *
 * @param value the raw `page_size`, as a query string, a number, or absent
 * @param fallback what to answer when the value is absent or unusable; defaults
 *   to {@link DEFAULT_LIST_PAGE_SIZE}
 * @returns an integer in `[1, MAX_LIST_PAGE_SIZE]`; `fallback`
 *   for anything absent, non-numeric, zero, or negative
 */
export function resolveListPageSize(value, fallback = DEFAULT_LIST_PAGE_SIZE) {
    const n = typeof value === 'string' ? Number.parseInt(value, 10) : value;
    if (typeof n !== 'number' || !Number.isFinite(n) || n < 1)
        return fallback;
    return Math.min(MAX_LIST_PAGE_SIZE, Math.floor(n));
}
/**
 * The largest page a **service** will read for any caller, including in-process ones.
 *
 * Not the same bound as {@link MAX_LIST_PAGE_SIZE} and deliberately so: they answer
 * different questions. `MAX_LIST_PAGE_SIZE` is *the most a client over HTTP may ask
 * for*, and 62.8.0 made it uniform so no route refuses a compliant request. This is
 * *the most any caller may read at once*, and its job is only to keep an unbounded
 * query from reaching MongoDB.
 *
 * **Collapsing the two truncates every export.** Twelve export routes in
 * `partner-marketplace` alone call a list method with `page_size: 10000` because a
 * CSV wants the register, not a page of it. Bounding a service at 200 would cap all
 * twelve at 200 rows, silently — the defect this sprint exists to remove, recreated
 * one layer down while removing it.
 *
 * **And four of those exports were already truncated**, which is how the number was
 * chosen rather than invented: `workflow-service` (templates and instances),
 * `report-service` and `partner-audit-service` clamped at 100, so their exports
 * asked for 10,000 rows and wrote 100. 10,000 is the value those call sites already
 * pass and the value `access-accountability`'s thirteen list methods already clamp
 * at, so it is the tree's existing answer to this question rather than a new one.
 */
export const MAX_QUERY_PAGE_SIZE = 10_000;
/**
 * The largest page a **client over HTTP** may ask a directory-shaped route for.
 *
 * ## Why a third number and not one of the two above
 *
 * `MAX_LIST_PAGE_SIZE` (200) is the right bound for a route whose client is a
 * paginating table. It is the **wrong** bound for the routes below, and applying
 * it to them would create the defect this programme exists to remove rather than
 * close one.
 *
 * Measured over `src/frontend/src` on this branch — the call sites, not an
 * intention. Tests and comment lines excluded, because a sentence about a page
 * size is not a request for one:
 *
 * ```
 * $ grep -rnE "page_size: '?500'?([^0-9]|$)" src --include=*.tsx --include=*.ts \
 *     | grep -v __tests__ | grep -vE ":[0-9]+: *(//|\*)" | wc -l
 * 69
 * ```
 *
 * **69 shipped call sites across 42 files** ask for exactly 500, and they are all
 * the same shape: a component loading a whole directory to turn an id into a
 * label, or a picker offering every parent record an operator may choose.
 * `ParentRecordPicker` asks it of eight routes; **16** of the 69 are
 * `listUsers`.
 *
 * Bounding those at 200 makes every row past the 200th render as *Unknown*, or
 * makes it unselectable in a picker with nothing on screen saying so. That is a
 * **silent truncation**, which {@link SELECTOR_PAGE_SIZE} already names as the
 * failure mode no larger number fixes — and it would be introduced by the very
 * change meant to bound the read.
 *
 * Leaving them on {@link MAX_QUERY_PAGE_SIZE} is the opposite error: 10,000 is a
 * *service* bound whose job is to stop an unbounded read from an in-process
 * export caller, and handing it to an HTTP client lets any holder of a read
 * permission pull 10,000 documents in one request.
 *
 * So this is the number those clients already send, declared once. It is not a
 * new product decision and it is deliberately not a fourth place to write `500`:
 * every route that needs it reads this, and the value is the measured one rather
 * than a chosen one — the same basis on which `MAX_QUERY_PAGE_SIZE` was set to
 * the 10,000 its call sites already passed.
 *
 * **The directory-label-map shape is still the wrong shape.** Fetching an entire
 * user directory to label a table is a design defect that a page size cannot fix,
 * and it is recorded as such rather than blessed by this constant existing. What
 * this constant fixes is the 500 that request currently returns.
 *
 * ## Sprint 63.14.0 AC8 — the premise re-examined, and it still holds
 *
 * `GET /users/directory` now exists and is the label map's read: `{_id, name,
 * email}` per row, 51,827 bytes for 500 users against `GET /users`' 199,388
 * (16 fields per row), measured on a `mongod 7.0.24` tenant written through
 * `UserService.create` — 63.14.0's work file, AC7. So the half of this
 * argument that was about the label map's SHAPE is answered.
 *
 * The bound does not move, for two reasons that are measurements rather than
 * caution:
 *
 * 1. **`GET /users` still has label-map clients.** 63.9.0's shrink-only
 *    baseline holds **78** components that call `listUsers` themselves, and the
 *    directory route changes none of them. Lowering this to 200 would make
 *    every user past the 200th render as *Unknown* on those — the silent
 *    truncation above, unchanged.
 * 2. **The directory's own page is this number.** `useUserDirectory`'s
 *    `DIRECTORY_PAGE_SIZE` IS this constant (AC9), and its `deriveDirectory`
 *    infers *complete* from a short page. Raising the bound for the directory
 *    alone would need the hook to ask for more, which is a payload decision
 *    this sprint measured but did not take: a user past the bound is resolved
 *    by the hook's lazy lookup, which since Sprint 63.14.1 (idea-catalog #345)
 *    is a batched `GET /users/directory?ids=` under this route's own grant, so
 *    a lookup-only role sees those names too.
 */
export const MAX_DIRECTORY_PAGE_SIZE = 500;
/**
 * The most ids `GET /users/directory?ids=` answers in one request — Sprint
 * 63.14.1, locked decision 2. The same bound as any list page
 * ({@link MAX_LIST_PAGE_SIZE}): the ids ride the query string, and a request is
 * refused above it rather than truncated, so a caller never mistakes a partial
 * answer for "the rest are not members". `useUserDirectory` splits a larger
 * batch into requests of this size.
 */
export const MAX_DIRECTORY_IDS = MAX_LIST_PAGE_SIZE;
/**
 * The longest id `GET /users/directory?ids=` accepts — Sprint 63.14.1. A user
 * is named by a ULID `_id` (26 characters) or a UUID `user_id` (36); the bound
 * leaves room for both and refuses anything that is plainly not an id. The
 * route refuses a request carrying a longer one, so `useUserDirectory` never
 * puts one in a batch: one bad value would otherwise fail every id beside it.
 */
export const MAX_DIRECTORY_ID_LENGTH = 128;
/**
 * The row bound for `GET /users/directory?ids=` — Sprint 63.14.1. At most two
 * rows answer each id, because an id is matched on `_id` OR `user_id` and the
 * two can name different rows; and at most {@link MAX_DIRECTORY_IDS} ids are
 * asked. The schema already refuses more ids than that; this clamps again at
 * the query, so the bound holds whoever calls the service
 * (`page-size-coercion-census` asks for a resolver on the path to `.limit`).
 */
export function resolveDirectoryIdsRowLimit(idCount) {
    const n = Number.isFinite(idCount) ? Math.floor(idCount) : 0;
    return Math.min(MAX_DIRECTORY_IDS, Math.max(1, n)) * 2;
}
/**
 * A page size for a **directory-shaped route**, bounded by
 * {@link MAX_DIRECTORY_PAGE_SIZE}.
 *
 * Use this at a route whose shipped clients legitimately ask for 500 — a picker
 * or a label map. Use {@link resolveListPageSize} at every other route; it is the
 * tighter default and the one a new route should reach for. Use
 * {@link resolveQueryPageSize} inside a service, where the caller may be an
 * export.
 *
 * @param value the raw `page_size`, as a query string, a number, or absent
 * @param fallback what to answer when the value is absent or unusable
 */
export function resolveDirectoryPageSize(value, fallback = DEFAULT_LIST_PAGE_SIZE) {
    const n = typeof value === 'string' ? Number.parseInt(value, 10) : value;
    if (typeof n !== 'number' || !Number.isFinite(n) || n < 1)
        return fallback;
    return Math.min(MAX_DIRECTORY_PAGE_SIZE, Math.floor(n));
}
/**
 * A page size for a **service**, bounded by {@link MAX_QUERY_PAGE_SIZE}.
 *
 * Use this inside a service. Use {@link resolveListPageSize} at the route, where the
 * caller is a client and the bound is the uniform one. A service that resolves with
 * the list bound cannot serve its own export path; a route that resolves with the
 * query bound lets a client ask for 10,000 rows.
 *
 * See {@link resolveListPageSize} for why `fallback` is a parameter.
 *
 * @param value the raw `page_size`, as a query string, a number, or absent
 * @param fallback what to answer when the value is absent or unusable
 */
export function resolveQueryPageSize(value, fallback = DEFAULT_LIST_PAGE_SIZE) {
    const n = typeof value === 'string' ? Number.parseInt(value, 10) : value;
    if (typeof n !== 'number' || !Number.isFinite(n) || n < 1)
        return fallback;
    return Math.min(MAX_QUERY_PAGE_SIZE, Math.floor(n));
}
/**
 * Bound the pagination parameters of a raw `req.query` at the **route**, leaving
 * every other filter untouched.
 *
 * ## Why this is one function and not one per module
 *
 * Sprint 64.61.0 fixed this defect in `partner-marketplace` with a local
 * `clientListQuery` helper — correct, and the right shape. Gate E12.5 then found
 * the same defect live on `assessments`, `contracts`, `data-mapping`, `incidents`
 * and `users`. Copying that helper into each of them would be five hand-written
 * copies of a bound, which is the *third bare copy* finding this same gate raised
 * against `ropa-completeness-service.ts` — committed while fixing it.
 *
 * ## An absent `page_size` stays absent, and that is the load-bearing part
 *
 * The obvious body writes `page_size: resolveDirectoryPageSize(q.page_size)`
 * unconditionally. That would hand every service a `20` when the client sent
 * nothing — and four data-mapping services (`categories`, `purposes`,
 * `retention`, `dm-audit`) document a default of **50**. Their shipped tables
 * would have quietly gone from 50 rows to 20, a behaviour change riding in under
 * a correctness fix where nobody would look for one. So the key is omitted rather
 * than defaulted, and each service keeps its own documented default.
 *
 * `page` is always resolved: every service defaults it to 1, there is nothing to
 * preserve, and `(page - 1) * page_size` on a string is the arithmetic that
 * silently *worked* while `.limit()` threw.
 */
export function boundDirectoryListQuery(query) {
    const raw = query.page_size;
    const sent = raw !== undefined && raw !== null && raw !== '';
    /*
     * fix-sprint-20260827-2129 residue (B-F4 class) — only string values pass.
     *
     * This spread the query unchanged, and Express 4's extended `qs` parser turns
     * `?status[$ne]=x` into `{ $ne: 'x' }` and `?status=a&status=b` into an array.
     * The services behind it assign those values straight onto Mongo filters (the
     * data-mapping registers, incidents, governance), so an operator object
     * reached the driver and a `search` object reached `$regex`. A filter value
     * is a string; anything else is dropped here, at the one boundary every
     * caller shares, so a service never sees it. A route that takes a structured
     * value (`ids`) parses it itself and sets it on the result afterwards.
     */
    const scalars = {};
    for (const [key, value] of Object.entries(query)) {
        if (typeof value === 'string')
            scalars[key] = value;
    }
    const out = {
        ...scalars,
        page: resolveListPage(query.page),
    };
    if (sent) {
        out.page_size = resolveDirectoryPageSize(raw);
    }
    else {
        /*
         * Deleted, not merely left unwritten. The spread above copies `page_size`
         * through when the client sent `?page_size=`, so a conditional *write* was
         * not enough — the empty string reached the service anyway, and the
         * "absent stays absent" contract this function documents was false for the
         * one input that most looks absent.
         *
         * Found by the assertion in
         * `assessments/__tests__/list-route-page-size-coercion.integration.test.ts`
         * ("omits page_size entirely when the client sent none"), which failed on
         * `{ page_size: '' }` on its first run. Worth recording: the test was
         * written to pin an intent, and it caught the intent not being implemented.
         */
        delete out.page_size;
    }
    return out;
}
/**
 * The 1-based page number, from the same string a query parameter arrives as.
 *
 * Unbounded above on purpose: a page beyond the end is an empty page, which is a
 * correct answer, whereas an unbounded `limit` is an unbounded read. The two
 * parameters are bounded for different reasons and only one of them needs a
 * ceiling.
 *
 * `(page - 1) * page_size` never threw the way `.limit()` did, because `-` and
 * `*` coerce a numeric string — which is why the defect above surfaced at
 * exactly one of the two call sites and looked like a Mongo problem.
 */
export function resolveListPage(value) {
    const n = typeof value === 'string' ? Number.parseInt(value, 10) : value;
    if (typeof n !== 'number' || !Number.isFinite(n) || n < 1)
        return 1;
    return Math.floor(n);
}
/**
 * Sprint 62.55.0 (gate E12.5 pass 2, R2-8) — a 0-based row offset into a list
 * a reader assembles from several store queries (the customs clock list pages
 * each segment in the store from an offset into the whole list).
 *
 * Unbounded above for the reason {@link resolveListPage} is: an offset beyond
 * the end reads an empty window, which is a correct answer. Anything absent,
 * non-numeric or negative is `0`.
 */
export function resolveListOffset(value) {
    const n = typeof value === 'string' ? Number.parseInt(value, 10) : value;
    if (typeof n !== 'number' || !Number.isFinite(n) || n < 0)
        return 0;
    return Math.floor(n);
}
//# sourceMappingURL=pagination.js.map